import { differenceInCalendarDays } from 'date-fns';
import { createDateSafe } from '@/utils/dateUtils';
import type { SegmentReviewState } from '../../utils/vacationSegments';

export const PERIODS_LABEL = 'Períodos';
export const NO_PERIODS_LABEL = 'Solicitação sem períodos registrados.';
export const MANAGER_LABEL = 'Gestor';

export const FACT_LABELS = {
    vacation: 'Férias',
    sell: 'Abono',
    deadline: 'Prazo',
} as const;

export const FACT_TONE_CLASSES: Record<string, string> = {
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700',
    danger: 'bg-red-50 text-red-700',
    muted: 'text-raro-gray-400',
};

export const DEADLINE_WARNING_DAYS = 30;

export const SELL_SUMMARY_LABELS: Record<string, string> = {
    None: 'Não solicitado',
    Pending: 'aguardando decisão',
    Approved: 'aprovado',
    Rejected: 'reprovado',
};

export function buildReturnLabel(returnDate: string): string {
    return `Volta ao trabalho em ${returnDate}`;
}

export function buildRequestedAtLabel(requestedAt: string): string {
    return `Pedido em ${requestedAt}`;
}

export function getSellTag(
    status: string
): { label: string; tone: 'warning' | 'success' | 'danger' } | null {
    if (status === 'None') return null;
    if (status === 'Pending') {
        return { label: 'aguardando decisão do DP', tone: 'warning' };
    }
    if (status === 'Approved') {
        return { label: 'aprovado', tone: 'success' };
    }
    return { label: 'reprovado', tone: 'danger' };
}

export function getDeadlineTag(
    deadlineDate: Date | string | null
): { label: string; tone: 'danger' | 'warning' | 'muted' } | null {
    if (!deadlineDate) return null;

    const date = typeof deadlineDate === 'string' ? createDateSafe(deadlineDate) : deadlineDate;
    if (!date) return null;
    const daysUntil = differenceInCalendarDays(date, new Date());

    if (daysUntil < 0) {
        const days = Math.abs(daysUntil);
        return { label: `vencido há ${days} ${days === 1 ? 'dia' : 'dias'}`, tone: 'danger' };
    }
    if (daysUntil === 0) {
        return { label: 'vence hoje', tone: 'danger' };
    }
    if (daysUntil <= DEADLINE_WARNING_DAYS) {
        return { label: `vence em ${daysUntil} ${daysUntil === 1 ? 'dia' : 'dias'}`, tone: 'warning' };
    }
    return { label: `vence em ${daysUntil} dias`, tone: 'muted' };
}

export const SEGMENT_REVIEW_META: Record<
    SegmentReviewState,
    { label: string; badgeClass: string; cardClass: string }
> = {
    pending: {
        label: 'Em análise',
        badgeClass: 'bg-raro-gray-100 text-raro-gray-700',
        cardClass: 'bg-primary-50/40 ring-raro-gray-100',
    },
    approved: {
        label: 'Validado',
        badgeClass: 'bg-emerald-100 text-emerald-700',
        cardClass: 'bg-emerald-50 ring-emerald-200',
    },
    rejected: {
        label: 'Reprovado',
        badgeClass: 'bg-red-100 text-red-700',
        cardClass: 'bg-red-50 ring-red-200',
    },
    corrected: {
        label: 'Corrigido · aguardando validação',
        badgeClass: 'bg-amber-100 text-amber-800',
        cardClass: 'bg-amber-50 ring-amber-200',
    },
};

export const CLIENT_REVIEW_LABELS: Partial<Record<SegmentReviewState, string>> = {
    approved: 'Validado pelo cliente',
    rejected: 'Reprovado pelo cliente',
};

export const PREVIOUS_DATES_LABEL = 'Data reprovada';
export const REJECTION_REASON_LABEL = 'Motivo da reprovação';
export const CORRECTION_RETURNED_LABEL = 'Gestor devolveu a correção';
